import { type Address, getAddress, numberToHex, SwitchChainError } from 'viem'
import { ChainNotConfiguredError, createConnector } from 'wagmi'
import { tempo } from 'wagmi/chains'

readOnly.type = 'readOnly' as const

/** Wagmi connector for read-only wallet viewing. Returns the target address for reads; throws on any signing action. */
export function readOnly(parameters: readOnly.Parameters) {
  const address = getAddress(parameters.address)
  let chainId = tempo.id as number

  return createConnector((config) => ({
    id: 'readOnly',
    name: 'Read Only',
    type: readOnly.type,

    async connect({ chainId: requestedChainId } = {}) {
      if (requestedChainId) chainId = requestedChainId
      return { accounts: [address], chainId } as never
    },

    async disconnect() {},

    async getAccounts() {
      return [address]
    },

    async getChainId() {
      return chainId
    },

    async isAuthorized() {
      return true
    },

    async switchChain({ chainId: newChainId }) {
      const chain = config.chains.find((c) => c.id === newChainId)
      if (!chain) throw new SwitchChainError(new ChainNotConfiguredError())
      chainId = newChainId
      config.emitter.emit('change', { chainId: newChainId })
      return chain
    },

    onAccountsChanged(accounts) {
      if (accounts.length === 0) this.onDisconnect()
      else config.emitter.emit('change', { accounts: accounts.map(getAddress) })
    },

    onChainChanged(chain) {
      config.emitter.emit('change', { chainId: Number(chain) })
    },

    async onDisconnect() {
      config.emitter.emit('disconnect')
    },

    async getProvider() {
      return {
        request({ method }: { method: string }) {
          if (method === 'eth_chainId') return numberToHex(chainId)
          if (method === 'eth_accounts') return [address]
          if (method === 'eth_requestAccounts') return [address]
          throw new Error('Cannot sign in view-only mode')
        },
      } as never
    },
  }))
}

export declare namespace readOnly {
  type Parameters = {
    address: Address
  }
}
